import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { SoftwareLogo, RatingStars } from "../components/SoftwareCard";
import { ArrowRight } from "lucide-react";

export default function Comparisons() {
  const [comparisons, setComparisons] = useState([]);
  const [bySlug, setBySlug] = useState({});

  useSEO({
    title: "Pool Service Software Comparisons",
    description: "Side-by-side comparisons of the top pool service software platforms: Skimmer vs Pool Brain, Jobber vs Housecall Pro, and more.",
    canonicalPath: "/comparisons",
    jsonLd: comparisons.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Pool Service Software Comparisons",
          itemListElement: comparisons.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.title, url: `${window.location.origin}/compare/${c.slug}` })),
        }
      : undefined,
  });

  useEffect(() => {
    api.get("/comparisons").then(({ data }) => setComparisons(data));
    api.get("/software").then(({ data }) => setBySlug(Object.fromEntries(data.map((s) => [s.slug, s]))));
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16" data-testid="comparisons-page">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Head to head</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Software Comparisons</h1>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
        Side-by-side breakdowns on pricing, chemistry, routes, and billing — with a clear recommendation at the end of each one.
      </p>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {comparisons.map((c) => {
          const [a, b] = (c.software_slugs || []).map((s) => bySlug[s]);
          return (
            <Link
              key={c.slug}
              to={`/compare/${c.slug}`}
              data-testid={`comparison-card-${c.slug}`}
              className="group flex flex-col bg-white rounded-2xl border border-border p-6 hover:border-sky-300 hover:shadow-lg hover:shadow-sky-100 transition-shadow duration-200"
            >
              {/* Contenders header */}
              <div className="flex items-center gap-3">
                {a && <SoftwareLogo software={a} size="w-12 h-12" />}
                <span className="font-heading font-black text-slate-300 text-sm shrink-0">VS</span>
                {b && <SoftwareLogo software={b} size="w-12 h-12" />}
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-sky-500 shrink-0 ml-auto transition-colors duration-200" aria-hidden="true" />
              </div>

              <h2 className="font-heading font-bold text-lg text-slate-900 group-hover:text-sky-600 transition-colors duration-200 mt-4">{c.title}</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed line-clamp-3 flex-1">{c.intro}</p>

              {/* Quick stats per contender */}
              {a && b && (
                <div className="mt-4 grid grid-cols-2 gap-3 pt-4 border-t border-border">
                  {[a, b].map((s) => (
                    <div key={s.slug} className="min-w-0">
                      <p className="font-heading font-semibold text-sm text-slate-800 truncate">{s.name}</p>
                      <div className="mt-1"><RatingStars rating={s.rating} reviewTotal={s.review_total} /></div>
                      <p className="text-xs text-slate-500 mt-1">{s.pricing_start}</p>
                    </div>
                  ))}
                </div>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
